import { CatButton, CatHeading } from 'components/blocks-components';
import { useTranslations } from 'next-intl';
import {
  Conditional,
  CustomStyledLabel,
  RequestServiceActionButtons,
  RequestServiceModal,
  useBreakpoint
} from '@cat-ecom/pcc-components';
import { PAGE_BREAKPOINTS } from 'constants/common';
import {
  REQUEST_SERVICE_CONFORMATION_WIDTH,
  SUCCESSFULL,
  FAILURE,
  REFERENCE_TYPE
} from '../constant';
import styles from '../request-equipment-service.module.scss';
import {
  replaceTokensWithComponents,
  capitalizeText
} from '@cat-ecom/common/utils/stringUtils';
import {
  Dispatch,
  useMemo,
  SetStateAction,
  useState,
  useTransition
} from 'react';
import cx from 'classnames';
import {
  partDetails,
  RequestServiceInfoPropsTypes,
  SelectedEquipmentInfoProps
} from '../types';
import { submitRequestServiceAction } from 'services/request-service/action';
import { getFormeventPayload, getRequestPayload } from '../utils';
import RequestServiceLoader from './request-service-loader';
import { useFormSubmitEvent } from 'hooks/analytics/use-form-submit-event';
import RequestServicePartDetails from './request-service-part-details';
import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import RequestEquipmentServiceFeedbackBanner from '../request-equipment-service-feedback-banner';
import RequestServicePreferredDateTimeInfo from './request-service-prefer-date-time-info';
import RequestServiceContactInfo from './request-service-contact-info';

interface RequestEquipmentServiceOverviewModalProps {
  isModalActive: boolean;
  setIsModalActive: Dispatch<SetStateAction<boolean>>;
  requestServiceInfo: RequestServiceInfoPropsTypes;
  equipmentDetails: SelectedEquipmentInfoProps;
  modalClose: () => void;
  resetFormOnModelClose: () => void;
  submitterEmail: string;
  partDetails: partDetails;
  type?: string;
}

const RequestEquipmentServiceOverviewModal = ({
  isModalActive,
  setIsModalActive,
  requestServiceInfo,
  equipmentDetails,
  modalClose,
  resetFormOnModelClose,
  submitterEmail,
  partDetails,
  type
}: Readonly<RequestEquipmentServiceOverviewModalProps>) => {
  const t = useTranslations();
  const { fireEvent } = useFormSubmitEvent();
  const isMd = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const fullWidth = isMd === false;
  const isEmurge = type === REFERENCE_TYPE.TECH_UPGRADE;
  const [requestServiceSubmitStatus, setRequestServiceSubmitStatus] =
    useState('');
  const requestServiceSubmitted = requestServiceSubmitStatus === SUCCESSFULL;
  const requestServiceSubmitError = requestServiceSubmitStatus === FAILURE;
  const [isLoading, startTransition] = useTransition();
  const onRequestModalClose = () => {
    setIsModalActive(false);
    setRequestServiceSubmitStatus('');
    requestServiceSubmitted && resetFormOnModelClose();
  };
  const { model, equipmentFamily = '', serialNumber = '' } = equipmentDetails;
  const serialLabel = serialNumber ? `${serialNumber} -` : '';
  const formData = useMemo(() => {
    return requestServiceInfo;
  }, [requestServiceInfo]);

  const handleClick = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.stopPropagation();
  };

  const updatedData = getRequestPayload(
    equipmentDetails,
    requestServiceInfo,
    submitterEmail,
    partDetails,
    type
  );
  const successHeading = t('REQ_SERV_SMRY_THANKYOU', {
    0: serialLabel,
    1: model,
    2: capitalizeText(equipmentFamily)
  });

  const errorHeading = t('UNKNOWN_ERROR_MESSAGE');
  const additionalContent = serialNumber ?? `${model} ${equipmentFamily}`;
  const formAdditionalContent = isEmurge && additionalContent;

  const handleSubmit = () => {
    startTransition(async () => {
      const { isSuccess } = await submitRequestServiceAction(updatedData);
      getFormeventPayload(
        'request service',
        formData,
        isSuccess,
        fireEvent,
        formAdditionalContent,
        false
      );
      if (isSuccess) {
        setRequestServiceSubmitStatus(SUCCESSFULL);
        modalClose();
      } else {
        setRequestServiceSubmitStatus(FAILURE);
      }
    });
  };

  return (
    <RequestServiceModal
      onBlModalClose={onRequestModalClose}
      isActive={isModalActive}
      disableBackdropClick={true}
      disableEscClick={true}
      fullWidth={fullWidth}
      width={REQUEST_SERVICE_CONFORMATION_WIDTH}
      className={styles['request-service-modal']}
      onClick={handleClick}
    >
      <div slot="header">
        <CatHeading slot="header" variant="title-sm">
          {!requestServiceSubmitted ? t('CONFIRMATION') : t('SUMMARY')}
        </CatHeading>
      </div>
      <Conditional test={isLoading}>
        <RequestServiceLoader />
      </Conditional>
      <Conditional test={!requestServiceSubmitted}>
        <Conditional test={requestServiceSubmitError}>
          <RequestEquipmentServiceFeedbackBanner
            variant="error"
            heading={errorHeading}
            classname="pb-4 px-3"
          />
        </Conditional>
        <div className={(styles['request-service__conf-modal'], 'px-3')}>
          <CatHeading variant="body-sm">
            <CustomStyledLabel>
              {replaceTokensWithComponents(
                t('REQ_SERV_CNFRM_MSG', {
                  0: '{0}',
                  1: '{1}',
                  2: '{2}',
                  3: '{3}'
                }),
                <span className="fw-bold">
                  {capitalizeText(formData.requestDealerName)}
                </span>,
                <span className="fw-bold text-uppercase">{serialLabel}</span>,
                <span className="fw-bold text-uppercase">{model}</span>,
                <span className="fw-bold">
                  {capitalizeText(equipmentFamily)}
                </span>
              )}
            </CustomStyledLabel>
          </CatHeading>
        </div>
      </Conditional>
      <div
        className={cx('px-3', {
          'pb-3': requestServiceSubmitted
        })}
      >
        <Conditional test={requestServiceSubmitted}>
          <div className="pb-2 pt-3">
            <RequestEquipmentServiceFeedbackBanner
              variant="success"
              heading={successHeading}
              description={t('REQ_SERV_SMRY_MSG')}
            />
          </div>
        </Conditional>
        <div
          className={cx([
            'mt-3 px-3 py-4',
            styles['request-service__container-conf']
          ])}
        >
          <Conditional test={!requestServiceSubmitted}>
            <div>
              <CatHeading variant="label">
                <CustomStyledLabel>
                  {t('SERVICE_REQUEST_INFORMATION')}:
                </CustomStyledLabel>
              </CatHeading>
              <div className="mt-2 text-break">
                <CatHeading variant="body-sm">
                  <CustomStyledLabel>
                    {formData.requestDescription}
                  </CustomStyledLabel>
                </CatHeading>
              </div>
            </div>
          </Conditional>
          <Conditional test={!isEmpty(partDetails)}>
            <div
              className={cx({
                'mt-4': !requestServiceSubmitted
              })}
            >
              <RequestServicePartDetails partDetails={partDetails} />
            </div>
          </Conditional>
          <div
            className={cx({
              'mt-4': !requestServiceSubmitted || !isEmpty(partDetails)
            })}
          >
            <RequestServicePreferredDateTimeInfo
              requestServiceInfo={formData}
            />
          </div>
          <div className="mt-4">
            <RequestServiceContactInfo requestServiceInfo={formData} />
          </div>
        </div>
      </div>
      <div slot="footer" className="d-flex">
        <Conditional test={!requestServiceSubmitted}>
          <RequestServiceActionButtons
            continueBtnProps={{
              fullWidth: true,
              onClick: () => {
                handleSubmit();
              },
              variant: 'primary'
            }}
            continueLabel={t('YES')}
            backLabel={t('NO')}
            isMD={fullWidth}
            backBtnProps={{
              fullWidth: true,
              onClick: onRequestModalClose
            }}
          />
        </Conditional>
        <Conditional test={requestServiceSubmitted}>
          <CatButton fullWidth={fullWidth} onClick={onRequestModalClose}>
            {t('CLOSE')}
          </CatButton>
        </Conditional>
      </div>
    </RequestServiceModal>
  );
};

export default RequestEquipmentServiceOverviewModal;
